/**
 * Analytics chart components for comprehensive data visualization
 * Provides reusable chart components for the analytics dashboard
 */
import React from 'react';
import { Chart } from 'react-google-charts';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';

// Interfaces
interface BaseChartProps {
    title: string;
    chartType: any; // react-google-charts type
    data: any[] | null;
    options: any;
    height?: string;
    loading?: boolean;
    error?: string | null;
    children?: React.ReactNode;
}

interface ChartComponentProps {
    data: any;
    loading?: boolean;
    error?: string | null;
}

interface EvaluationVolumeChartProps extends ChartComponentProps {
    volumeType?: 'monthly' | 'quarterly' | 'company';
}

interface TimeSeriesChartProps extends ChartComponentProps {
    granularity?: 'daily' | 'weekly' | 'monthly';
}

interface OverviewData {
    totalPresentations: number;
    completedPresentations: number;
    inProgressPresentations: number;
    failedPresentations: number;
    averageScore: number;
    completionRate: number;
}

interface OverviewMetricsProps {
    overview: OverviewData | null;
    loading?: boolean;
    error?: string | null;
}

/**
 * Base chart component with error handling and loading states
 */
const BaseChart: React.FC<BaseChartProps> = ({
    title,
    chartType,
    data,
    options,
    height = "400px",
    loading = false,
    error = null,
    children
}) => {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
                        <CircularProgress />
                    </Box>
                ) : data && data.length > 1 ? (
                    <Chart
                        chartType={chartType}
                        data={data}
                        options={options}
                        width="100%"
                        height={height}
                    />
                ) : (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height,
                        color: 'text.secondary'
                    }}>
                        <Typography variant="body2">
                            No data available for visualization
                        </Typography>
                    </Box>
                )}

                {children}
            </CardContent>
        </Card>
    );
};

/**
 * Score distribution chart component
 */
export const ScoreDistributionChart: React.FC<ChartComponentProps> = ({ data, loading, error }) => {
    const options = {
        title: 'Distribution of Evaluation Scores',
        backgroundColor: 'transparent',
        colors: ['#1976d2', '#388e3c', '#f57c00', '#ff9800', '#d32f2f'],
        chartArea: { left: 60, top: 40, width: '80%', height: '75%' },
        hAxis: {
            title: 'Score Range (0-5 scale)',
            titleTextStyle: { fontSize: 12 }
        },
        vAxis: {
            title: 'Number of Presentations',
            titleTextStyle: { fontSize: 12 }
        },
        legend: { position: 'none' },
        bar: { groupWidth: '70%' }
    };

    return (
        <BaseChart
            title="Score Distribution"
            chartType="ColumnChart"
            data={data}
            options={options}
            loading={loading}
            error={error}
        />
    );
};

/**
 * Time series performance chart component
 */
export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ data, loading, error, granularity = 'daily' }) => {
    const options = {
        title: `Performance Trends (${granularity})`,
        backgroundColor: 'transparent',
        colors: ['#1976d2', '#388e3c'],
        chartArea: { left: 80, top: 40, width: '80%', height: '75%' },
        hAxis: {
            title: granularity === 'daily' ? 'Date' : granularity === 'weekly' ? 'Week' : 'Month',
            titleTextStyle: { fontSize: 12 },
            slantedText: true,
            slantedTextAngle: 45
        },
        vAxes: {
            0: {
                title: 'Average Score',
                titleTextStyle: { fontSize: 12, color: '#1976d2' },
                textStyle: { color: '#1976d2' }
            },
            1: {
                title: 'Count',
                titleTextStyle: { fontSize: 12, color: '#388e3c' },
                textStyle: { color: '#388e3c' }
            }
        },
        seriesType: 'columns',
        series: {
            0: { type: 'line', targetAxisIndex: 0, lineWidth: 3, pointSize: 6 },
            1: { type: 'columns', targetAxisIndex: 1 }
        },
        legend: { position: 'bottom' }
    };

    return (
        <BaseChart
            title="Performance Over Time"
            chartType="ComboChart"
            data={data}
            options={options}
            loading={loading}
            error={error}
            height="350px"
        />
    );
};

/**
 * Consultant comparison chart component
 */
export const ConsultantComparisonChart: React.FC<ChartComponentProps> = ({ data, loading, error }) => {
    const options = {
        title: 'Consultant Performance Comparison',
        backgroundColor: 'transparent',
        colors: ['#1976d2', '#388e3c', '#f57c00'],
        chartArea: { left: 150, top: 40, width: '70%', height: '75%' },
        hAxis: {
            title: 'Average Score',
            titleTextStyle: { fontSize: 12 },
            minValue: 0,
            maxValue: 5
        },
        vAxis: {
            title: 'Consultants',
            titleTextStyle: { fontSize: 12 }
        },
        legend: {
            position: 'bottom',
            textStyle: { fontSize: 11 }
        },
        bar: { groupWidth: '60%' }
    };

    return (
        <BaseChart
            title="Top Performing Consultants"
            chartType="BarChart"
            data={data}
            options={options}
            loading={loading}
            error={error}
            height="400px"
        />
    );
};

/**
 * Evaluation volume chart component
 */
export const EvaluationVolumeChart: React.FC<EvaluationVolumeChartProps> = ({ data, loading, error, volumeType = 'monthly' }) => {
    const titleMap = {
        monthly: 'Monthly Evaluation Volume',
        quarterly: 'Quarterly Evaluation Volume',
        company: 'Evaluation Volume by Company'
    };

    const options = {
        title: titleMap[volumeType],
        backgroundColor: 'transparent',
        colors: ['#1976d2', '#388e3c', '#f57c00', '#ff9800', '#d32f2f'],
        chartArea: { left: 80, top: 40, width: '80%', height: '75%' },
        hAxis: {
            title: volumeType === 'company' ? 'Company' : 'Period',
            titleTextStyle: { fontSize: 12 },
            slantedText: volumeType === 'company',
            slantedTextAngle: volumeType === 'company' ? 45 : 0
        },
        vAxis: {
            title: 'Number of Evaluations',
            titleTextStyle: { fontSize: 12 }
        },
        legend: { position: 'none' },
        bar: { groupWidth: '70%' }
    };

    return (
        <BaseChart
            title="Evaluation Volume"
            chartType="ColumnChart"
            data={data}
            options={options}
            loading={loading}
            error={error}
        />
    );
};

/**
 * Competency progression chart component
 */
export const CompetencyProgressionChart: React.FC<ChartComponentProps> = ({ data, loading, error }) => {
    const options = {
        title: 'Competency Improvement Over Time',
        backgroundColor: 'transparent',
        colors: ['#388e3c', '#1976d2', '#f57c00'],
        chartArea: { left: 80, top: 40, width: '80%', height: '75%' },
        hAxis: {
            title: 'Improvement Percentage',
            titleTextStyle: { fontSize: 12 }
        },
        vAxis: {
            title: 'Consultant - Category',
            titleTextStyle: { fontSize: 12 }
        },
        legend: {
            position: 'bottom',
            textStyle: { fontSize: 11 }
        },
        bar: { groupWidth: '70%' }
    };

    // Transform data for better display
    const processedData = data ? [
        ['Consultant-Category', 'Improvement %', 'First Score', 'Latest Score']
    ].concat(
        data.slice(1).map((row: any[]) => [
            `${row[0]} - ${row[1]}`, // Combine consultant and category
            row[2], // Improvement %
            row[3], // First score
            row[4]  // Last score
        ])
    ) : null;

    return (
        <BaseChart
            title="Competency Progression"
            chartType="BarChart"
            data={processedData}
            options={options}
            loading={loading}
            error={error}
            height="450px"
        />
    );
};

/**
 * Category performance pie chart component
 */
export const CategoryPerformanceChart: React.FC<ChartComponentProps> = ({ data, loading, error }) => {
    const options = {
        title: 'Performance by Evaluation Category',
        backgroundColor: 'transparent',
        colors: ['#1976d2', '#388e3c', '#f57c00'],
        chartArea: { left: 20, top: 40, width: '90%', height: '75%' },
        pieHole: 0.4,
        legend: {
            position: 'bottom',
            textStyle: { fontSize: 12 }
        },
        pieSliceText: 'percentage',
        sliceVisibilityThreshold: 0.02
    };

    return (
        <BaseChart
            title="Category Performance Distribution"
            chartType="PieChart"
            data={data}
            options={options}
            loading={loading}
            error={error}
            height="350px"
        />
    );
};

/**
 * Overview metrics component with KPI cards
 */
export const OverviewMetrics: React.FC<OverviewMetricsProps> = ({ overview, loading, error }) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!overview) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                No overview data available
            </Alert>
        );
    }

    const metrics = [
        { label: 'Total Presentations', value: overview.totalPresentations, color: 'primary.main' },
        { label: 'Completed', value: overview.completedPresentations, color: 'success.main' },
        { label: 'In Progress', value: overview.inProgressPresentations, color: 'warning.main' },
        { label: 'Failed', value: overview.failedPresentations, color: 'error.main' },
        { label: 'Average Score', value: `${overview.averageScore.toFixed(1)}/5`, color: 'info.main' },
        { label: 'Completion Rate', value: `${overview.completionRate.toFixed(1)}%`, color: 'secondary.main' }
    ];

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, p: 2 }}>
            {metrics.map((metric, index) => (
                <Card key={index}>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: metric.color, mb: 1 }}>
                            {metric.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {metric.label}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};
